import { useEffect, useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LogOut, Upload, Loader2, ArrowLeft, Trash2, FileText, Sheet, PackageSearch,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => "US$ " + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const unit = (n) => "US$ " + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(Number(n)) < 10 ? 4 : 2 });
const qty = (n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 3 });
const fdate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

async function logoDataUrl() {
  try {
    const res = await fetch("/logo-tecnomyl.png");
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

export default function PedidosSection({ user, onLogout, onBack }) {
  const fileRef = useRef(null);
  const topRef = useRef(null);
  const botRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cliente, setCliente] = useState("");
  const [vendedor, setVendedor] = useState("__all__");
  const [producto, setProducto] = useState("");
  const [pendMin, setPendMin] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (cliente.trim()) params.cliente = cliente.trim();
      if (vendedor !== "__all__") params.vendedor = vendedor;
      if (producto.trim()) params.producto = producto.trim();
      if (pendMin !== "" && !isNaN(Number(pendMin))) params.pendiente_min = Number(pendMin);
      const res = await api.get("/pedidos", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  }, [cliente, vendedor, producto, pendMin]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    if (fileRef.current) fileRef.current.value = "";
    const tid = toast.loading("Procesando la planilla de pedidos…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/pedidos/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message || "Planilla importada", { id: tid });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Borrar todos los pedidos cargados?")) return;
    setBusy(true);
    try {
      await api.delete("/pedidos");
      toast.success("Datos borrados");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo borrar");
    } finally {
      setBusy(false);
    }
  };

  const rows = data?.rows || [];
  useEffect(() => {
    const id = setTimeout(() => { if (botRef.current) setScrollW(botRef.current.scrollWidth); }, 120);
    return () => clearTimeout(id);
  }, [rows]);
  const syncFromTop = () => { if (botRef.current && topRef.current) botRef.current.scrollLeft = topRef.current.scrollLeft; };
  const syncFromBot = () => { if (topRef.current && botRef.current) topRef.current.scrollLeft = botRef.current.scrollLeft; };
  const COLS = [
    ["Cliente", (r) => r.cliente],
    ["Vendedor", (r) => r.vendedor],
    ["Producto", (r) => r.producto],
    ["Pedido", (r) => r.pedido],
    ["F. Emisión", (r) => fdate(r.fecha_emision)],
    ["Vencimiento", (r) => fdate(r.vencimiento)],
    ["Cantidad", (r) => qty(r.cantidad)],
    ["Unitario", (r) => unit(r.unitario)],
    ["Total", (r) => money(r.total)],
    ["Entregado", (r) => qty(r.entregado)],
    ["Val$ Entreg.", (r) => money(r.valor_entregado)],
    ["Pendiente", (r) => qty(r.pendiente)],
    ["Val$ a entregar", (r) => money(r.valor_a_entregar)],
  ];

  const exportExcel = () => {
    const aoa = [COLS.map((c) => c[0])];
    rows.forEach((r) => aoa.push(COLS.map((c) => c[1](r))));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = COLS.map((c) => ({ wch: c[0] === "Cliente" || c[0] === "Producto" ? 34 : c[0] === "Vendedor" ? 26 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pedidos");
    XLSX.writeFile(wb, "Pedidos_de_Ventas.xlsx");
  };

  const exportPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    doc.setFillColor(15, 82, 51);
    doc.rect(0, 0, W, 60, "F");
    const logo = await logoDataUrl();
    if (logo) { try { doc.addImage(logo, "PNG", 32, 12, 84, 36); } catch { /* ignore */ } }
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Pedidos de Ventas", W - 32, 30, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Tecnomyl S.A. — ${rows.length} pedidos`, W - 32, 46, { align: "right" });
    autoTable(doc, {
      startY: 72,
      head: [COLS.map((c) => c[0])],
      body: rows.map((r) => COLS.map((c) => c[1](r))),
      styles: { fontSize: 6.5, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [15, 82, 51] },
      columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 80 }, 2: { cellWidth: 90 } },
      margin: { left: 20, right: 20 },
    });
    doc.save("Pedidos_de_Ventas.pdf");
  };

  const t = data?.totals;

  return (
    <div className="min-h-screen bg-[#f4f7f5]" data-testid="pedidos-section">
      <header className="bg-[#0f5233] text-white sticky top-0 z-40 shadow">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-2 py-1"><Logo /></div>
            <div className="flex items-center gap-2">
              <PackageSearch className="h-5 w-5" />
              <span className="font-semibold text-lg">Pedidos de Ventas</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-xs text-emerald-100">{user?.email}</span>
            {onBack && (
              <Button size="sm" variant="secondary" onClick={onBack} data-testid="pedidos-back-btn" className="gap-2">
                <ArrowLeft className="h-4 w-4" /> Volver
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={onLogout} data-testid="pedidos-logout-btn" className="gap-2">
              <LogOut className="h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".xls,.xlsx,.csv" className="hidden" onChange={handleUpload} data-testid="pedidos-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pedidos-upload-btn" className="h-9 gap-2 bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Cargar Excel
          </Button>
          <Button onClick={exportExcel} disabled={!rows.length} variant="outline" data-testid="pedidos-export-excel" className="h-9 gap-2">
            <Sheet className="h-4 w-4 text-emerald-600" /> Excel
          </Button>
          <Button onClick={exportPDF} disabled={!rows.length} variant="outline" data-testid="pedidos-export-pdf" className="h-9 gap-2">
            <FileText className="h-4 w-4 text-red-600" /> PDF
          </Button>
          <Button onClick={handleDelete} disabled={busy || !rows.length} variant="outline" data-testid="pedidos-delete-btn" className="h-9 gap-2 text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="h-4 w-4" /> Borrar
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Cliente</label>
            <Input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Buscar cliente…" className="h-9 w-52" data-testid="pedidos-filter-cliente" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Vendedor</label>
            <Select value={vendedor} onValueChange={setVendedor}>
              <SelectTrigger className="h-9 w-56" data-testid="pedidos-filter-vendedor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los vendedores</SelectItem>
                {(data?.vendedores || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Producto</label>
            <Input value={producto} onChange={(e) => setProducto(e.target.value)} placeholder="Buscar producto…" className="h-9 w-52" data-testid="pedidos-filter-producto" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-slate-500">Cant. pendiente ≥</label>
            <Input type="number" value={pendMin} onChange={(e) => setPendMin(e.target.value)} placeholder="0" className="h-9 w-36" data-testid="pedidos-filter-pendiente" />
          </div>
        </div>

        {data && rows.length > 0 && t && (
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="rounded-full bg-slate-100 text-slate-700 px-3 py-1 font-semibold">{data.count} pedidos</span>
            <span className="rounded-full bg-slate-100 text-slate-700 px-3 py-1">Total: {money(t.total)}</span>
            <span className="rounded-full bg-emerald-50 text-emerald-800 px-3 py-1">Entregado: {money(t.valor_entregado)}</span>
            <span className="rounded-full bg-amber-50 text-amber-800 px-3 py-1 font-semibold" data-testid="pedidos-total-pendiente">Pendiente: {money(t.valor_a_entregar)}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
        ) : !rows.length ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="pedidos-empty">
            No hay pedidos cargados. Subí el <b>Listado de Pedidos de Ventas</b> en Excel.
          </div>
        ) : (
          <div data-testid="pedidos-table-wrap">
            <div ref={topRef} onScroll={syncFromTop} className="overflow-x-auto rounded-t-lg border border-b-0 border-slate-200 bg-slate-100" data-testid="pedidos-top-scroll">
              <div style={{ width: scrollW || 1, height: 14 }} />
            </div>
            <div ref={botRef} onScroll={syncFromBot} className="bg-white rounded-b-xl border border-slate-200 overflow-x-auto" data-testid="pedidos-table">
              <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-[#0f5233] text-white sticky top-0">
                <tr>
                  {COLS.map((c, i) => (
                    <th key={i} className={`px-3 py-2 font-semibold ${i >= 6 ? "text-right" : "text-left"}`}>{c[0]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50/60" : ""} data-testid="pedidos-row">
                    {COLS.map((c, j) => (
                      <td key={j} className={`px-3 py-1.5 ${j >= 6 ? "text-right tabular-nums" : "text-left"} ${j === 11 && Number(r.pendiente) > 0 ? "font-semibold text-amber-700" : "text-slate-700"}`}>
                        {c[1](r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
